import { createContext, use, type ReactNode } from "react";
import type { FileUploader } from "./media/fileUploader.ts";
import type { ImageCompressor } from "./media/imageCompressor.ts";
import type { QrSource } from "./qr/qrSource.ts";
import type { TelegramLogin } from "./telegram/telegramLogin.ts";

/** Всё, через что приложение выходит в сеть и к браузерным API. В тестах подменяется фейками. */
export type AppEnvironment = {
  apiBaseUrl: string;
  fetch: (request: Request) => Promise<Response>;
  WebSocket: typeof WebSocket;
  /** Окно Telegram Login; `null` — вход через Telegram выключен в конфигурации. */
  telegramLogin: TelegramLogin | null;
  /** Камера и распознавание QR для встроенного сканера привязки. */
  qrSource: QrSource;
  /** Сжатие фото перед загрузкой. */
  imageCompressor: ImageCompressor;
  /** PUT вложения по подписанной ссылке хранилища, с прогрессом. */
  uploadFile: FileUploader;
  /** Таймеры переподключения App WS и порога «Нестабильное соединение»; тест двигает их время вручную. */
  timers: Timers;
  /** Системное «Поделиться» (Web Share API); `null` — браузер его не умеет. */
  share: ((data: ShareData) => Promise<void>) | null;
  /** Записать текст в буфер обмена. */
  copyText: (text: string) => Promise<void>;
};

export type Timers = {
  setTimeout: (callback: () => void, ms: number) => number;
  clearTimeout: (id: number | undefined) => void;
};

const EnvironmentContext = createContext<AppEnvironment | null>(null);

export function EnvironmentProvider({ environment, children }: { environment: AppEnvironment; children: ReactNode }) {
  return <EnvironmentContext value={environment}>{children}</EnvironmentContext>;
}

/** Сырое окружение без авторизации; API-клиент для запросов к backend — `useApiClient` из модуля сессии. */
export function useAppEnvironment(): AppEnvironment {
  const environment = use(EnvironmentContext);
  if (!environment) throw new Error("EnvironmentProvider is missing");
  return environment;
}
